import { Box, Typography } from '@mui/material';
import type { NextCuePreviewMeta } from '@/helpers/resolve-next-cue-preview-meta.helper';
import { formatPreviewDuration } from '@/helpers/resolve-next-cue-preview-meta.helper';
import { useVideoDurationSeconds } from '@/hooks/use-video-duration-seconds';
import { useTranslations } from '@/i18n/use-translations';

const metaTextSx = {
  color: '#fff',
  textShadow: '0 0 1px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.75)',
  lineHeight: 1.25,
  userSelect: 'none' as const,
};

/** Bottom-left operator metadata for the Next cue preview. */
export function FlowControlNextCueMetaOverlay({ meta }: { meta: NextCuePreviewMeta }) {
  const t = useTranslations();
  const durationSeconds = useVideoDurationSeconds(meta.isVideo ? meta.videoUrl : undefined);
  const durationLabel = formatPreviewDuration(durationSeconds);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 10,
        bottom: 8,
        right: 56,
        zIndex: 1,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.25,
        minWidth: 0,
      }}
    >
      <Typography
        component="span"
        sx={{
          ...metaTextSx,
          fontWeight: 700,
          fontSize: '0.8rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {meta.segmentTitle}
      </Typography>
      <Typography
        component="span"
        sx={{
          ...metaTextSx,
          fontWeight: 600,
          fontSize: '0.75rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {meta.itemTypeLabel}
        {meta.itemName ? ` · ${meta.itemName}` : ''}
      </Typography>
      {meta.isVideo ? (
        <Typography component="span" sx={{ ...metaTextSx, fontWeight: 500, fontSize: '0.7rem' }}>
          {durationLabel
            ? t('flows.detail.control.nowNext.duration', {
                defaultValue: 'Length {{duration}}',
                duration: durationLabel,
              })
            : t('flows.detail.control.nowNext.durationPending', { defaultValue: 'Length …' })}
        </Typography>
      ) : null}
    </Box>
  );
}
